import React from 'react'; // Import React

import { ArrowLeftIcon, ArrowRightIcon } from '@chakra-ui/icons';
import { Flex, HStack } from '@chakra-ui/layout';
import { format } from 'date-fns';
import { useQuery } from 'react-query';

import quickListingApi from 'api/quicklisting';
import AppModal from 'components/modules/AppModal';
import Typography from 'components/primitives/Typography';
import { FETCH_QUICK_LISTING_DETAILS } from 'constants/queries';
import ucFirstMultiple from 'utils/string/ucFirstMultiple';

import { QuickListingDetailsProps } from './QuickListingDetails.props';

const QuickListingDetailView = ({
  id,
  ...props
}: QuickListingDetailsProps): JSX.Element => {
  // Fetch the quick listing details using the id and react-query
  const { data: fullDetails } = useQuery(
    `${FETCH_QUICK_LISTING_DETAILS}${id}`,
    () => (id ? quickListingApi.getQuickListingDetails(id) : undefined)
  );

  /**
   * Format price range in a human-readable format (e.g. "$1.2M - $2.3M")
   * @param range - The price range string from the API (e.g. "1200000-2300000")
   * @returns formatted price range string
   */
  const formatPriceRange = (range: string): string => {
    if (!range) return '-';

    const [min, max] = range.split('-').map((val) => Number(val.trim()));

    // Helper function to format number into readable price
    const format = (num: number) => {
      if (num >= 1_000_000_000) return `$${(num / 1_000_000_000).toFixed(1)}B`;
      if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
      if (num >= 1_000) return `$${(num / 1_000).toFixed(1)}K`;
      return `$${num}`;
    };

    return `${format(min)} - ${format(max)}`;
  };

  return (
    <AppModal
      {...props}
      size="3xl"
      onClose={props.onClose}
      onOverlayClick={props.onClose}
      title={fullDetails ? `${fullDetails?.region}` : ''}
    >
      {/* Right Navigation Arrow */}
      {!props.disableNext && (
        <Flex
          position="absolute"
          right="-150px"
          top="20vh"
          width="60px"
          height="60px"
          borderRadius="30px"
          zIndex="100"
          backgroundColor="#fff"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          onClick={props.onNext}
        >
          <ArrowRightIcon />
        </Flex>
      )}

      {/* Left Navigation Arrow */}
      {!props.disablePrev && (
        <Flex
          position="absolute"
          left="-150px"
          top="20vh"
          width="60px"
          height="60px"
          borderRadius="30px"
          zIndex="100"
          backgroundColor="#fff"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          onClick={props.onPrev}
        >
          <ArrowLeftIcon />
        </Flex>
      )}

      {/* Modal Content */}
      <Flex flexDirection="column">
        <HStack spacing={3} alignItems="flex-start" height={480}>
          <Flex
            flexDir="column"
            border="1px solid #ddd"
            p={4}
            flex={4}
            height="100%"
          >
            {/* Section Header */}
            <Flex
              justifyContent="space-between"
              alignItems="center"
              borderBottom="1px solid #ddd"
              m={-4}
              px={4}
              py={2}
              mb={4}
            >
              <Typography variant="type8" color="shade5" weight="600">
                QUICK LISTING DETAILS
              </Typography>
            </Flex>

            {/* Property Details */}
            <Flex flexDirection="column">
              {/* Property Type and Address Row */}
              <Flex flexDirection="row" flex={1}>
                <Flex flexDirection="column" flex={1}>
                  <Typography variant="type10" color="shade1" weight="600">
                    PROPERTY TYPE
                  </Typography>
                  <Typography variant="type8" color="shade5">
                    {ucFirstMultiple(fullDetails?.property_type ?? '-')}
                  </Typography>
                </Flex>
              </Flex>

              <Flex flexDirection="row" flex={1}>
                <Flex flexDirection="column" flex={1} mt={8}>
                  <Typography variant="type10" color="shade1" weight="600">
                    ADDRESS
                  </Typography>
                  <Typography variant="type8" color="shade5">
                    {fullDetails?.formattedAddress ?? '-'}
                  </Typography>
                </Flex>
              </Flex>

              {/* Second Row: Estimated Value, Bedrooms, and Car Garage */}
              <Flex flexDirection="row" flex={1} mt={8}>
                <Flex flexDirection="column" flex={1}>
                  <Typography variant="type10" color="shade1" weight="600">
                    ESTIMATED VALUE
                  </Typography>
                  <Typography variant="type8" color="shade5">
                    {fullDetails?.market_price
                      ? formatPriceRange(fullDetails?.market_price)
                      : '-'}
                  </Typography>
                </Flex>

                <Flex flexDirection="column" flex={1}>
                  <Typography variant="type10" color="shade1" weight="600">
                    BEDROOMS
                  </Typography>
                  <Typography variant="type8" color="shade5">
                    {fullDetails?.bedroom ?? 0}
                  </Typography>
                </Flex>

                <Flex flexDirection="column" flex={1}>
                  <Typography variant="type10" color="shade1" weight="600">
                    Car Garage
                  </Typography>
                  <Typography variant="type8" color="shade5">
                    {fullDetails?.garage ? `${fullDetails?.garage} Bay` : '-'}
                  </Typography>
                </Flex>
              </Flex>

              {/* Third Row: Matches and Buyer Match Date */}
              <Flex flexDirection="row" flex={1} mt={8}>
                <Flex flexDirection="column" flex={1}>
                  <Typography variant="type10" color="shade1" weight="600">
                    Matches
                  </Typography>
                  <Typography variant="type8" color="shade5">
                    {fullDetails?.matchCount ? fullDetails?.matchCount : 0}
                  </Typography>
                </Flex>

                <Flex flexDirection="column" flex={1}>
                  <Typography variant="type10" color="shade1" weight="600">
                    Buyer Match Date
                  </Typography>
                  <Typography variant="type8" color="shade5">
                    {fullDetails?.createdAt
                      ? format(
                          new Date(fullDetails?.createdAt),
                          'MM/dd/y HH:mm:ss a'
                        )
                      : '-'}{' '}
                  </Typography>
                </Flex>
              </Flex>
              {/* Fourth Row: Matches and Buyer Match Date */}
              <Flex
                flexDirection="row"
                flex={1}
                mt={8}
                borderTop={1}
                borderStyle={'solid'}
                borderColor="gray.300"
                pt={8}
              >
                <Flex flexDirection="column" flex={1}>
                  <Typography variant="type10" color="shade1" weight="600">
                    CONVERTED TO REGISTER
                  </Typography>
                  <Typography variant="type8" color="shade5">
                    {fullDetails?.user ? `Yes` : `No`}
                  </Typography>
                </Flex>

                <Flex flexDirection="column" flex={1}>
                  <Typography variant="type10" color="shade1" weight="600">
                    REGISTERED ON
                  </Typography>
                  <Typography variant="type8" color="shade5">
                    {fullDetails?.user && fullDetails?.user.createdAt
                      ? format(
                          new Date(fullDetails?.createdAt),
                          'MM/dd/y HH:mm:ss a'
                        )
                      : '-'}
                  </Typography>
                </Flex>
              </Flex>
              <Flex flexDirection="row" marginTop={8}>
                <Flex flexDirection="column" flex={1}>
                  <Typography variant="type10" color="shade1" weight="600">
                    NAME
                  </Typography>
                  <Typography variant="type8" color="shade5">
                    {fullDetails?.user
                      ? fullDetails?.user.first_name +
                        ' ' +
                        fullDetails?.user.first_name
                      : '-'}
                  </Typography>
                </Flex>

                <Flex flexDirection="column" flex={1}>
                  <Typography variant="type10" color="shade1" weight="600">
                    EMAIL
                  </Typography>
                  <Typography variant="type8" color="shade5">
                    {fullDetails?.user ? fullDetails?.user.email : '-'}
                  </Typography>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </HStack>
      </Flex>
    </AppModal>
  );
};

export default QuickListingDetailView;
